import { useCallback, useMemo, useState } from 'react'

import { Check, ChevronDown, X } from 'lucide-react'
import type { FC } from 'react'

import { Badge } from '@/components/ui/badge'
import { buttonVariants } from '@/components/ui/button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
	CommandSeparator,
} from '@/components/ui/command'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface RunOption {
	label: string
	value: string
}

interface AnalyticsRunFilterProps {
	/** Available runs to select from */
	runs: RunOption[]
	/** Currently selected run IDs */
	selectedRunIds: string[]
	/** Callback when selection changes */
	onChange: (runIds: string[]) => void
	/** Maximum number of runs that can be selected (default: 10) */
	maxSelections?: number
	/** Whether the filter is disabled */
	disabled?: boolean
	/** Label shown when collapsed */
	label?: string
}

const MAX_RUNS = 10

export const AnalyticsRunFilter: FC<AnalyticsRunFilterProps> = ({
	runs,
	selectedRunIds,
	onChange,
	maxSelections = MAX_RUNS,
	disabled = false,
	label = 'Runs',
}) => {
	const [open, setOpen] = useState(false)

	const sortedRuns = useMemo(() => {
		// Runs should already be sorted by runNumber descending (newest first)
		return runs.slice(0, 100) // Limit displayed runs for performance
	}, [runs])

	const selectedCount = selectedRunIds.length
	const hasReachedMax = selectedCount >= maxSelections

	const handleToggleRun = useCallback(
		(runId: string) => {
			const isSelected = selectedRunIds.includes(runId)
			if (isSelected) {
				onChange(selectedRunIds.filter(id => id !== runId))
			} else if (!hasReachedMax) {
				onChange([...selectedRunIds, runId])
			}
		},
		[selectedRunIds, onChange, hasReachedMax]
	)

	const handleSelectLatest = useCallback(() => {
		if (sortedRuns.length > 0) {
			onChange([sortedRuns[0].value])
		}
	}, [sortedRuns, onChange])

	const handlePreset = useCallback(
		(count: number) => {
			const presetRunIds = sortedRuns
				.slice(0, count)
				.map(run => run.value)
			onChange(presetRunIds)
		},
		[sortedRuns, onChange]
	)

	const handleClear = useCallback(() => {
		onChange([])
	}, [onChange])

	const selectedLabels = useMemo(() => {
		return selectedRunIds
			.map(id => {
				const run = runs.find(r => r.value === id)
				return run?.label
			})
			.filter(Boolean)
	}, [selectedRunIds, runs])

	const displayText = useMemo(() => {
		if (selectedCount === 0) {
			return 'Select runs...'
		}
		if (selectedCount === 1) {
			return `Run #${selectedLabels[0]}`
		}
		return `${selectedCount} runs selected`
	}, [selectedCount, selectedLabels])

	return (
		<div className="flex items-center gap-2">
			<span className="text-sm text-muted-foreground hidden sm:inline">
				{label}:
			</span>
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger
					className={cn(
						buttonVariants({ variant: 'outline' }),
						'h-8 min-w-[140px] justify-between'
					)}
					role="combobox"
					aria-expanded={open}
					disabled={disabled || runs.length === 0}
				>
					<span className="truncate">{displayText}</span>
					<ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</PopoverTrigger>
				<PopoverContent className="w-[250px] p-0" align="start">
					<Command shouldFilter={false}>
						<CommandList>
							<CommandEmpty>No runs available.</CommandEmpty>

							{/* Quick select options */}
							<CommandGroup heading="Quick Select">
								<CommandItem
									value="latest"
									onSelect={() => {
										handleSelectLatest()
									}}
								>
									<Check
										className={cn(
											'mr-2 h-4 w-4',
											selectedCount === 1 &&
												selectedRunIds[0] ===
													sortedRuns[0]?.value
												? 'opacity-100'
												: 'opacity-0'
										)}
									/>
									Latest run only
								</CommandItem>
								<CommandItem
									value="last5"
									onSelect={() => {
										handlePreset(5)
									}}
								>
									<Check
										className={cn(
											'mr-2 h-4 w-4',
											selectedCount === 5
												? 'opacity-100'
												: 'opacity-0'
										)}
									/>
									Last 5 runs
									{sortedRuns.length < 5 && (
										<span className="ml-1 text-xs text-muted-foreground">
											({sortedRuns.length} available)
										</span>
									)}
								</CommandItem>
								<CommandItem
									value="last10"
									onSelect={() => {
										handlePreset(10)
									}}
								>
									<Check
										className={cn(
											'mr-2 h-4 w-4',
											selectedCount === 10
												? 'opacity-100'
												: 'opacity-0'
										)}
									/>
									Last 10 runs
									{sortedRuns.length < 10 && (
										<span className="ml-1 text-xs text-muted-foreground">
											({sortedRuns.length} available)
										</span>
									)}
								</CommandItem>
								{selectedCount > 0 && (
									<CommandItem
										value="clear"
										onSelect={() => {
											handleClear()
										}}
										className="text-destructive"
									>
										<X className="mr-2 h-4 w-4" />
										Clear selection
									</CommandItem>
								)}
							</CommandGroup>

							<CommandSeparator />

							{/* Individual runs */}
							<CommandGroup
								heading={`All Runs (max ${maxSelections})`}
							>
								{sortedRuns.map(run => {
									const isSelected = selectedRunIds.includes(
										run.value
									)
									const isDisabledItem =
										!isSelected && hasReachedMax
									return (
										<CommandItem
											key={run.value}
											value={`run-${run.value}`}
											onSelect={() => {
												if (!isDisabledItem) {
													handleToggleRun(run.value)
												}
											}}
											className={cn(
												isDisabledItem && 'opacity-50'
											)}
										>
											<div
												className={cn(
													'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
													isSelected
														? 'bg-primary text-primary-foreground'
														: 'opacity-50 [&_svg]:invisible'
												)}
											>
												<Check className="h-3 w-3" />
											</div>
											Run #{run.label}
										</CommandItem>
									)
								})}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			{/* Selected badges */}
			{selectedCount > 0 && selectedCount <= 3 && (
				<div className="hidden sm:flex items-center gap-1">
					{selectedLabels.map((lbl, idx) => (
						<Badge
							key={selectedRunIds[idx]}
							variant="secondary"
							className="text-xs"
						>
							#{lbl}
						</Badge>
					))}
				</div>
			)}

			{hasReachedMax && (
				<span className="text-xs text-muted-foreground hidden sm:inline">
					(max {maxSelections})
				</span>
			)}
		</div>
	)
}

export default AnalyticsRunFilter
